/** Adjuntos de un movimiento: lista, subida (también con la cámara del móvil) y apertura. */
import { useRef, useState } from 'react';
import { ActionIcon, Button, Group, Paper, Stack, Text, Tooltip } from '@mantine/core';
import { IconCamera, IconEye, IconPaperclip, IconUnlink } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { abrirDocumento, api } from '../api';
import { avisarError, avisarOk, fechaCorta, mostrarAvisos } from '../comun';
import type { Documento } from '../tipos';

export const ACEPTA_ARCHIVOS = 'application/pdf,image/*,.xlsx,.docx,.odt,.ods,.csv,.txt';

export async function subirDocumento(archivo: File, campos: Record<string, string | null | undefined>): Promise<Documento> {
  const f = new FormData();
  for (const [k, v] of Object.entries(campos)) if (v) f.append(k, v);
  f.append('archivo', archivo, archivo.name);
  return api.subir<Documento>('/documentos', f);
}

export function Adjuntos({ entidad, entidadId, categoria, titulo, fecha }: { entidad: 'ingreso' | 'gasto' | 'declaracion'; entidadId: string; categoria: string; titulo: string; fecha?: string | null }) {
  const qc = useQueryClient();
  const clave = ['documentos', entidad, entidadId];
  const docs = useQuery({ queryKey: clave, queryFn: () => api.get<Documento[]>(`/documentos?estado=todos&entidad=${entidad}&entidadId=${entidadId}`) });
  const [subiendo, setSubiendo] = useState(false);
  const entradaArchivo = useRef<HTMLInputElement>(null);
  const entradaCamara = useRef<HTMLInputElement>(null);

  const subir = async (archivo: File | undefined) => {
    if (!archivo) return;
    setSubiendo(true);
    try {
      const d = await subirDocumento(archivo, { categoria, titulo, fechaDocumento: fecha ?? undefined, entidad, entidadId });
      mostrarAvisos([d.aviso]);
      avisarOk('Documento adjuntado');
      await qc.invalidateQueries({ queryKey: clave });
      await qc.invalidateQueries({ queryKey: [entidad === 'gasto' ? 'gastos' : 'ingresos'] });
    } catch (e) {
      avisarError(e);
    } finally {
      setSubiendo(false);
      if (entradaArchivo.current) entradaArchivo.current.value = '';
      if (entradaCamara.current) entradaCamara.current.value = '';
    }
  };

  const desvincular = async (id: string) => {
    try {
      await api.post(`/documentos/${id}/desvincular`, { entidad, entidadId });
      await qc.invalidateQueries({ queryKey: clave });
    } catch (e) {
      avisarError(e);
    }
  };

  return (
    <Paper withBorder p="sm">
      <Stack gap="xs">
        <Group justify="space-between">
          <Text fw={600} size="sm">
            Justificantes
          </Text>
          <Group gap="xs">
            <input ref={entradaArchivo} type="file" accept={ACEPTA_ARCHIVOS} hidden onChange={(e) => void subir(e.currentTarget.files?.[0])} />
            <input ref={entradaCamara} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void subir(e.currentTarget.files?.[0])} />
            <Button size="xs" variant="light" leftSection={<IconPaperclip size={14} />} loading={subiendo} onClick={() => entradaArchivo.current?.click()}>
              Adjuntar
            </Button>
            <Button size="xs" variant="light" leftSection={<IconCamera size={14} />} loading={subiendo} onClick={() => entradaCamara.current?.click()} hiddenFrom="sm">
              Foto
            </Button>
          </Group>
        </Group>
        {docs.data?.length ? (
          docs.data.map((d) => (
            <Group key={d.id} justify="space-between" wrap="nowrap">
              <div style={{ minWidth: 0 }}>
                <Text size="sm" truncate>
                  {d.titulo}
                </Text>
                <Text size="xs" c="dimmed" truncate>
                  {d.nombreArchivo} · {fechaCorta(d.creadoEn.slice(0, 10))}
                </Text>
              </div>
              <Group gap={4} wrap="nowrap">
                <Tooltip label="Ver">
                  <ActionIcon variant="subtle" onClick={() => void abrirDocumento(d.id).catch(avisarError)}>
                    <IconEye size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Quitar de este movimiento (el documento se conserva)">
                  <ActionIcon variant="subtle" color="gray" onClick={() => void desvincular(d.id)}>
                    <IconUnlink size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>
          ))
        ) : (
          <Text size="xs" c="dimmed">
            Sin documentos adjuntos.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
